import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import GameScreenShell from "@components/Home/Games/GameScreenShell";
import SessionSummary, { SessionSummaryStats } from "@components/Home/Games/SessionSummary";
import type { GameSessionGame } from "@models/game";
import { GAME_IDS } from "@models/game";
import {
  useStartSessionMutation,
  useSubmitRoundMutation,
  useCompleteSessionMutation,
  useGetContentQuery,
} from "@store/gameApi";
import { RootState } from "@store/store";
import {
  type NBackStimulusItem,
  getStimulusPool,
  buildNBackSequence,
  computeNBackRoundMetrics,
} from "./nBackContent";
import { getContentBankLabel } from "@utils/contentBankTranslations";
import {
  gameContinueButtonSx,
  gamePrimaryButtonSx,
} from "@components/Home/Games/gameUi";
import { useGameTimingMode } from "@hooks/useGameTimingMode";
import { adjustNBackTiming } from "@utils/gameTiming";
import {
  buildImageCandidateUrls,
  preloadImageUrls,
} from "@utils/gameImage";

export interface NBackGuidedProps {
  guidedSessionId?: string;
  guidedCurrentGame?: GameSessionGame;
  guidedGameIndex?: number;
  guidedTotalGames?: number;
  onGuidedGameComplete?: () => void;
}

const ROUNDS_PER_SESSION = 3;
const SESSION_START_DEDUPE_MS = 2000;

let lastSessionStartAt = 0;

type Phase = "idle" | "playing" | "feedback";

function getLevelParams(level: number) {
  const clamped = Math.max(1, Math.min(10, level));
  if (clamped <= 2)
    return { n: 1, sequenceLength: 15, displayMs: 2300, showLabels: true, showHistory: true };
  if (clamped <= 4)
    return { n: 1, sequenceLength: 20, displayMs: 2300, showLabels: false, showHistory: true };
  if (clamped <= 6)
    return { n: 2, sequenceLength: 15, displayMs: 2200, showLabels: false, showHistory: false };
  if (clamped <= 8)
    return { n: 2, sequenceLength: 20, displayMs: 1650, showLabels: false, showHistory: false };
  const n = clamped === 10 ? 4 : 3;
  return { n, sequenceLength: 20, displayMs: 1350, showLabels: false, showHistory: false };
}

interface FreePlayLocationState {
  freePlaySessionId?: string;
  freePlayGames?: GameSessionGame[];
}

const NBack: FC<NBackGuidedProps> = ({
  guidedSessionId,
  guidedCurrentGame,
  guidedGameIndex = 0,
  guidedTotalGames = 1,
  onGuidedGameComplete,
}) => {
  const location = useLocation();
  const freePlayState = location.state as FreePlayLocationState | null;
  const freePlaySessionId = freePlayState?.freePlaySessionId;
  const freePlayGame = freePlayState?.freePlayGames?.[0];

  const isGuided = Boolean(guidedSessionId && guidedCurrentGame);
  const isFreePlayFromLibrary = Boolean(freePlaySessionId && freePlayGame);
  const effectiveSessionId = guidedSessionId ?? freePlaySessionId ?? null;
  const effectiveGame = guidedCurrentGame ?? freePlayGame;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId
  );
  const sessionStartedRef = useRef(false);
  const [startSession] = useStartSessionMutation();
  const [sessionStartError, setSessionStartError] = useState<string | null>(null);
  const [submitRound] = useSubmitRoundMutation();
  const [completeSession] = useCompleteSessionMutation();

  const contentSeed = effectiveGame?.contentSeed ?? "";
  const difficulty = effectiveGame ? Math.max(1, Math.min(10, effectiveGame.difficulty)) : 1;
  const gameTimingMode = useGameTimingMode();
  const { levelParams, isiMs } = useMemo(() => {
    const base = getLevelParams(difficulty);
    const { params, isiMs: isi } = adjustNBackTiming(base, gameTimingMode);
    return { levelParams: params, isiMs: isi };
  }, [difficulty, gameTimingMode]);

  const { data: contentData, error: contentError } = useGetContentQuery(
    { gameId: GAME_IDS.nBack, seed: contentSeed },
    { skip: !contentSeed }
  );

  const pool = useMemo(
    () => getStimulusPool(contentSeed, 0, contentData, contentError),
    [contentSeed, contentData, contentError]
  );

  const [sessionId, setSessionId] = useState<string | null>(effectiveSessionId);
  const [phase, setPhase] = useState<Phase>("idle");
  const [roundCount, setRoundCount] = useState(0);
  const [stimulusSequence, setStimulusSequence] = useState<number[]>([]);
  const [matchPositionsSet, setMatchPositionsSet] = useState<Set<number>>(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [roundStartTime, setRoundStartTime] = useState(0);
  const [stimulusOnAt, setStimulusOnAt] = useState(0);
  const [truePositives, setTruePositives] = useState(0);
  const [falsePositives, setFalsePositives] = useState(0);
  const [reactionTimes, setReactionTimes] = useState<number[]>([]);
  const [tapFeedback, setTapFeedback] = useState<"none" | "correct" | "falsePositive">("none");
  const [xpEarned, setXpEarned] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [sessionScore, setSessionScore] = useState(0);
  const [roundResults, setRoundResults] = useState<{ accuracy: number; reactionMs: number }[]>([]);
  const [domainUpdatesSum, setDomainUpdatesSum] = useState<Record<string, number>>({});
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [summaryStats, setSummaryStats] = useState<SessionSummaryStats | null>(null);
  const [muted, setMuted] = useState(false);
  const [imageAttemptById, setImageAttemptById] = useState<Record<string, number>>(
    {}
  );

  const startNewRound = useCallback(() => {
    const seed = contentSeed ? `${contentSeed}-${roundCount}` : `local-${roundCount}`;
    const { n, sequenceLength } = levelParams;
    const { sequence, matchPositions } = buildNBackSequence(
      pool.length,
      n,
      sequenceLength,
      seed
    );
    const now = Date.now();
    setStimulusSequence(sequence);
    setMatchPositionsSet(matchPositions);
    setCurrentIndex(0);
    setRoundStartTime(now);
    setTruePositives(0);
    setFalsePositives(0);
    setReactionTimes([]);
    setTapFeedback("none");
    setXpEarned(null);
    setPhase("playing");
    setStimulusOnAt(now);
    setImageAttemptById({});
  }, [contentSeed, roundCount, levelParams, pool.length]);

  useEffect(() => {
    if (pool.length === 0) return;
    const urls = pool.flatMap((item) =>
      buildImageCandidateUrls(item.imageUrl)
    );
    const controller = new AbortController();
    preloadImageUrls(urls, { concurrency: 4, signal: controller.signal }).catch(
      () => {}
    );
    return () => controller.abort();
  }, [pool]);

  useEffect(() => {
    if (isGuided || isFreePlayFromLibrary) return;
    if (!selectedPatientId) {
      setSessionStartError(t("games.errors.onlyPatientCanPlay"));
      return;
    }
    if (sessionStartedRef.current) return;
    const now = Date.now();
    if (now - lastSessionStartAt < SESSION_START_DEDUPE_MS) return;
    lastSessionStartAt = now;
    sessionStartedRef.current = true;
    setSessionStartError(null);
    let mounted = true;
    startSession({ patientId: selectedPatientId })
      .unwrap()
      .then((res) => {
        if (mounted && res?.sessionId) {
          setSessionId(res.sessionId);
          setSessionStartTime(Date.now());
        }
      })
      .catch((err: { status?: number }) => {
        if (mounted && err?.status === 403) {
          setSessionStartError(t("games.errors.onlyPatientCanPlay"));
        }
      });
    return () => {
      mounted = false;
    };
  }, [isGuided, isFreePlayFromLibrary, selectedPatientId, startSession, t]);

  useEffect(() => {
    if ((isGuided && guidedSessionId) || isFreePlayFromLibrary) {
      setSessionStartTime(Date.now());
    }
  }, [isGuided, guidedSessionId, isFreePlayFromLibrary]);

  useEffect(() => {
    if (sessionStartTime == null || paused) return;
    const id = setInterval(() => {
      setTimerSeconds(Math.floor((Date.now() - sessionStartTime) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [sessionStartTime, paused]);

  // Use a polling interval so progress isn't cancelled by timer/shell re-renders (which cleared timeouts)
  useEffect(() => {
    if (phase !== "playing" || stimulusSequence.length === 0 || paused) return;
    const displayMs = levelParams.displayMs;
    const interval = setInterval(() => {
      setCurrentIndex((idx) => {
        if (idx >= stimulusSequence.length) return idx;
        const displayStart = idx === 0 ? stimulusOnAt : stimulusOnAt + isiMs;
        const elapsed = Date.now() - displayStart;
        if (idx > 0 && elapsed < 0) return idx; // still in blank
        if (elapsed >= displayMs) {
          setStimulusOnAt(Date.now());
          return idx + 1;
        }
        return idx;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [
    phase,
    stimulusSequence.length,
    stimulusOnAt,
    currentIndex,
    levelParams.displayMs,
    isiMs,
    paused,
  ]);

  // When currentIndex reaches end, switch to feedback
  useEffect(() => {
    if (phase !== "playing" || stimulusSequence.length === 0) return;
    if (currentIndex >= stimulusSequence.length) {
      setPhase("feedback");
    }
  }, [phase, currentIndex, stimulusSequence.length]);

  const handleTapMatch = useCallback(() => {
    if (phase !== "playing" || currentIndex >= stimulusSequence.length) return;
    const isMatch = matchPositionsSet.has(currentIndex);
    const reactionMs = Date.now() - stimulusOnAt;

    if (isMatch) {
      setTruePositives((c) => c + 1);
      setReactionTimes((arr) => [...arr, reactionMs]);
      setTapFeedback("correct");
    } else {
      setFalsePositives((c) => c + 1);
      setTapFeedback("falsePositive");
    }
    setTimeout(() => setTapFeedback("none"), 400);
  }, [phase, currentIndex, stimulusSequence.length, matchPositionsSet, stimulusOnAt]);


  const handleNextRound = useCallback(() => {
    const nextRound = roundCount + 1;
    const { accuracy, missed, errorCount, avgReactionMs, reactionTimeMs } =
      computeNBackRoundMetrics({
        matchCount: matchPositionsSet.size,
        truePositives,
        falsePositives,
        reactionTimes,
      });

    setRoundResults((prev) => [...prev, { accuracy, reactionMs: avgReactionMs }]);

    if (sessionId) {
      submitRound({
        sessionId,
        body: {
          gameId: GAME_IDS.nBack,
          roundNumber: nextRound,
          difficulty,
          accuracy,
          reactionTimeMs,
          completionTimeMs: Date.now() - roundStartTime,
          errorCount,
          rawData: {
            truePositives,
            falsePositives,
            missedMatches: missed,
            reactionTimes,
          },
        },
      })
        .unwrap()
        .then((res) => {
          if (res?.xpEarned != null) {
            setXpEarned(res.xpEarned);
            setSessionScore((s) => s + res.xpEarned);
          }
          if (res?.domainUpdates && Object.keys(res.domainUpdates).length > 0) {
            setDomainUpdatesSum((prev) => {
              const next = { ...prev };
              for (const [k, v] of Object.entries(res.domainUpdates)) {
                next[k] = (next[k] ?? 0) + v;
              }
              return next;
            });
          }
        })
        .catch(() => {});
    } else {
      const xp = accuracy >= 0.8 ? 20 : 10;
      setXpEarned(xp);
      setSessionScore((s) => s + xp);
    }

    setRoundCount(nextRound);

    if (nextRound >= ROUNDS_PER_SESSION) {
      if (isGuided && onGuidedGameComplete) {
        onGuidedGameComplete();
        return;
      }
      const allResults = [...roundResults, { accuracy, reactionMs: avgReactionMs }];
      const totalRounds = allResults.length;
      const avgAccuracy =
        totalRounds > 0
          ? (allResults.reduce((s, r) => s + r.accuracy, 0) / totalRounds) * 100
          : 0;
      const avgReactionSec =
        totalRounds > 0
          ? allResults.reduce((s, r) => s + r.reactionMs, 0) / totalRounds / 1000
          : 0;

      if (sessionId) {
        completeSession({ sessionId })
          .unwrap()
          .then((res) => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgReactionSec,
              xpEarned: res?.totalXp ?? sessionScore,
              domainChanges: domainUpdatesSum,
            });
          })
          .catch(() => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgReactionSec,
              xpEarned: sessionScore,
              domainChanges: domainUpdatesSum,
            });
          });
      } else {
        setSummaryStats({
          accuracyPercent: avgAccuracy,
          avgReactionSeconds: avgReactionSec,
          xpEarned: sessionScore,
          domainChanges: domainUpdatesSum,
        });
      }
      lastSessionStartAt = 0;
      setSessionId(null);
      setShowSessionSummary(true);
      return;
    }
    startNewRound();
  }, [
    roundCount,
    truePositives,
    falsePositives,
    matchPositionsSet.size,
    reactionTimes,
    roundStartTime,
    sessionId,
    isGuided,
    onGuidedGameComplete,
    completeSession,
    submitRound,
    startNewRound,
    roundResults,
    sessionScore,
    domainUpdatesSum,
    difficulty,
  ]);

  const handleExit = useCallback(() => {
    if (sessionId) completeSession({ sessionId }).catch(() => {});
    lastSessionStartAt = 0;
    navigate(-1);
  }, [sessionId, completeSession, navigate]);

  const handleSummaryContinue = useCallback(() => navigate(-1), [navigate]);

  const currentStimulus: NBackStimulusItem | null =
    phase === "playing" &&
    currentIndex < stimulusSequence.length &&
    pool.length > 0
      ? pool[stimulusSequence[currentIndex] % pool.length]
      : null;
  const currentStimulusCandidates = currentStimulus
    ? buildImageCandidateUrls(currentStimulus.imageUrl)
    : [];
  const currentStimulusAttempt = currentStimulus
    ? imageAttemptById[currentStimulus.id] ?? 0
    : 0;
  const currentStimulusSrc =
    currentStimulusCandidates[
      Math.min(currentStimulusAttempt, Math.max(0, currentStimulusCandidates.length - 1))
    ];

  const showStimulus = phase === "playing" && currentIndex < stimulusSequence.length;
  const isBlank =
    showStimulus &&
    currentIndex > 0 &&
    Date.now() - stimulusOnAt < isiMs;

  const feedbackText =
    phase === "feedback"
      ? t("games.nBack.feedbackCorrect")
      : tapFeedback === "falsePositive"
        ? t("games.nBack.feedbackFalsePositive")
        : null;

  const progress =
    phase === "idle"
      ? 0
      : (roundCount + (phase === "feedback" ? 1 : 0)) / ROUNDS_PER_SESSION;

  const nLabel = t("games.nBack.nBackLabel", { n: levelParams.n });
  const helperText =
    levelParams.n === 1
      ? t("games.nBack.helperText1")
      : t("games.nBack.helperTextN", { n: levelParams.n });

  if (showSessionSummary && summaryStats) {
    return (
      <SessionSummary stats={summaryStats} onContinue={handleSummaryContinue} />
    );
  }

  return (
    <GameScreenShell
      gameName={t("games.nBack.title")}
      level={difficulty}
      progress={progress}
      feedback={feedbackText}
      timerSeconds={sessionStartTime != null ? timerSeconds : null}
      score={sessionScore > 0 ? sessionScore : null}
      paused={paused}
      onBack={handleExit}
      onPause={() => setPaused((p) => !p)}
      gameIndex={isGuided ? guidedGameIndex : undefined}
      totalGames={isGuided ? guidedTotalGames : undefined}
      muted={muted}
      onMuteChange={setMuted}
    >
      {phase === "idle" && (
        <Box
          textAlign="center"
          py={4}
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {sessionStartError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {sessionStartError}
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            {t("games.nBack.instructionsTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t("games.nBack.instructions")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={startNewRound}
            sx={gameContinueButtonSx}
          >
            {t("games.nBack.startRound")}
          </Button>
        </Box>
      )}

      {(phase === "playing" || phase === "feedback") && (
        <Box
          sx={{
            flex: 1,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 2, sm: 2.5 },
            minHeight: 0,
          }}
        >
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: "1.5rem", mb: 0, textAlign: "center" }}
          >
            {nLabel}
          </Typography>
          {levelParams.n <= 3 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0, textAlign: "center" }}
            >
              {helperText}
            </Typography>
          )}
          {levelParams.n > 3 && <Box sx={{ height: 0 }} />}

          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: { xs: "min(86vw, 340px)", sm: "min(62vw, 280px)", md: 280 },
              minWidth: { xs: "min(86vw, 340px)", sm: "min(62vw, 280px)", md: 280 },
              borderRadius: 2,
              border: "3px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              flexShrink: 0,
              minHeight: { xs: "min(58vh, 340px)", sm: "min(52vh, 280px)", md: 280 },
            }}
          >
            {showStimulus && !isBlank && currentStimulus && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                sx={{
                  gap: { xs: 0.5, sm: 0.75 },
                  animation: tapFeedback === "falsePositive" ? "nback-shake 0.4s ease" : undefined,
                  "@keyframes nback-shake": {
                    "0%,100%": { transform: "translateX(0)" },
                    "25%": { transform: "translateX(-6px)" },
                    "75%": { transform: "translateX(6px)" },
                  },
                }}
              >
                <Box
                  sx={{
                    width: { xs: "min(80vw, 320px)", sm: "min(56vw, 250px)", md: 250 },
                    height: { xs: "min(80vw, 320px)", sm: "min(56vw, 250px)", md: 250 },
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {currentStimulusSrc ? (
                    <Box
                      component="img"
                      src={currentStimulusSrc}
                      alt=""
                      onError={() =>
                        setImageAttemptById((prev) => ({
                          ...prev,
                          [currentStimulus.id]: Math.min(
                            (prev[currentStimulus.id] ?? 0) + 1,
                            Math.max(0, currentStimulusCandidates.length - 1)
                          ),
                        }))
                      }
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Typography variant="h6" color="text.secondary">
                      {getContentBankLabel(t, currentStimulus.id, currentStimulus.label).slice(0, 1).toUpperCase()}
                    </Typography>
                  )}
                </Box>
                {levelParams.showLabels && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0,
                      fontSize: { xs: "0.98rem", sm: "0.95rem" },
                      fontWeight: 600,
                      textAlign: "center",
                      lineHeight: 1.15,
                    }}
                    color="text.secondary"
                  >
                    {getContentBankLabel(t, currentStimulus.id, currentStimulus.label)}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {levelParams.showHistory && phase === "playing" && levelParams.n > 0 && (
            <Box
              display="flex"
              justifyContent="center"
              gap={0.5}
              flexWrap="wrap"
              sx={{ mt: 0, minHeight: 56 }}
            >
              {Array.from({ length: levelParams.n }, (_, i) => {
                const idx = currentIndex - 1 - levelParams.n + i;
                const item =
                  idx >= 0 && idx < stimulusSequence.length && pool.length > 0
                    ? pool[stimulusSequence[idx] % pool.length]
                    : null;
                return (
                  <Box
                    key={i}
                    sx={{
                      width: { xs: 48, sm: 44 },
                      height: { xs: 48, sm: 44 },
                      borderRadius: 1,
                      bgcolor: item ? "primary.light" : "action.hover",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    {item &&
                      (() => {
                        const imageCandidates = buildImageCandidateUrls(item.imageUrl);
                        const imageAttempt = imageAttemptById[item.id] ?? 0;
                        const imageSrc =
                          imageCandidates[
                            Math.min(imageAttempt, Math.max(0, imageCandidates.length - 1))
                          ];
                        return imageSrc ? (
                        <Box
                          component="img"
                          src={imageSrc}
                          alt=""
                          onError={() =>
                            setImageAttemptById((prev) => ({
                              ...prev,
                              [item.id]: Math.min(
                                (prev[item.id] ?? 0) + 1,
                                Math.max(0, imageCandidates.length - 1)
                              ),
                            }))
                          }
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                        ) : (
                        <Typography variant="caption" color="primary.contrastText">
                          {getContentBankLabel(t, item.id, item.label).slice(0, 1)}
                        </Typography>
                        );
                      })()}
                  </Box>
                );
              })}
            </Box>
          )}

          {phase === "playing" && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleTapMatch}
              sx={{ ...gamePrimaryButtonSx, alignSelf: "center", mb: 1 }}
            >
              {t("games.nBack.tapMatch")}
            </Button>
          )}

          {phase === "feedback" && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
              mt={0}
              mb={0}
              flexShrink={0}
              sx={{ mb: 1 }}
            >
              {xpEarned != null && (
                <Typography variant="body2" color="primary.main">
                  +{xpEarned} XP
                </Typography>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={handleNextRound}
                sx={gameContinueButtonSx}
              >
                {roundCount + 1 >= ROUNDS_PER_SESSION
                  ? t("games.nBack.finishSession")
                  : t("games.nBack.nextRound")}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </GameScreenShell>
  );
};

export default NBack;
